// "In THIS track, what did she write?"
//
// Lives beside Show.jsx rather than in pages/App/Coach/components/ because it
// is track-specific: it renders step numbers and track-scoped copy, neither of
// which means anything to the member page's LessonAnswersCard.
//
// This track's courses only. "Everything this person ever answered" already has
// a home one click away, and the footer says so; mixing the two would make the
// dialog disagree with the checklist it sits beside.
//
// Ordering is server-owned — track step, then lesson.position, then
// question.position — and is never re-sorted here.
import { useState } from "react"
import { Link } from "@inertiajs/react"
import { ChevronDown, ChevronRight, EyeOff, Loader2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Copied from LessonAnswersCard's QuestionRow rather than extracted. A shared
// primitive on the second use, with a third caller unproven, is premature.
function QuestionRow({ question }) {
  const answered = !!question.responseText

  return (
    <li className="py-2 border-t border-zinc-100 first:border-t-0">
      <p className="text-xs text-zinc-500">{question.text}</p>
      {answered ? (
        <>
          <p className="text-sm text-zinc-800 whitespace-pre-line mt-1">{question.responseText}</p>
          {question.answeredAt && (
            <p className="text-xs text-zinc-400 mt-1">Answered {formatDate(question.answeredAt)}</p>
          )}
        </>
      ) : (
        // The coach's real job on a certification track is spotting the blank,
        // so an unanswered question is rendered, never omitted.
        <p className="text-sm text-zinc-400 italic mt-1">Not answered yet</p>
      )}
    </li>
  )
}

function LessonBlock({ lesson }) {
  return (
    <div className="rounded-md bg-zinc-50 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
          {lesson.title}
        </span>
        {lesson.coachOnly && (
          <Badge variant="outline" className="border-amber-500 text-amber-600 shrink-0">
            <EyeOff className="h-3 w-3 mr-1" />
            Coach
          </Badge>
        )}
        <span className="text-xs text-zinc-500 tabular-nums shrink-0">
          {lesson.answeredCount} of {lesson.questionCount} answered
        </span>
      </div>
      <ul className="mt-1">
        {lesson.questions.map((question) => (
          <QuestionRow key={question.id} question={question} />
        ))}
      </ul>
    </div>
  )
}

// Same collapse as LessonAnswersCard and CourseHistoryCard: useState +
// aria-expanded + a chevron. No accordion primitive — neither accordion.jsx nor
// collapsible.jsx exists in components/ui, and one dialog is not a reason to
// add a dependency the pages around it do not use.
function CourseBlock({ course, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen)
  const answered = course.lessons.reduce((sum, l) => sum + l.answeredCount, 0)
  const total = course.lessons.reduce((sum, l) => sum + l.questionCount, 0)

  return (
    <div className="rounded-md border border-zinc-200">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-zinc-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0" />
        )}
        <span className="w-5 text-right text-xs text-zinc-400 tabular-nums shrink-0">
          {course.step}.
        </span>
        <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
          {course.title}
        </span>
        <span className="text-xs text-zinc-500 tabular-nums shrink-0">
          {total === 0 ? "No questions" : `${answered} of ${total} answered`}
        </span>
      </button>
      {open && total > 0 && (
        <div className="border-t border-zinc-100 px-3 py-2 space-y-2">
          {course.lessons.map((lesson) => (
            <LessonBlock key={lesson.id} lesson={lesson} />
          ))}
        </div>
      )}
    </div>
  )
}

export function TrackAnswersDialog({ open, onOpenChange, answers, member, trackTitle }) {
  // The prop is shared by every row, so a stale one from the previously opened
  // member is the failure mode to guard: until `memberAnswers` is for THIS
  // person, the dialog is loading, not wrong.
  const loaded = !!answers && answers.memberId === member?.id
  const courses = loaded ? answers.courses : []
  const memberName = member?.name || answers?.memberName

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <ResponsiveDialogHeader>
          {/* The member's name leads: the coach arrived here holding a person,
              not a track. */}
          <ResponsiveDialogTitle>
            {memberName} — {trackTitle}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            What {memberName} wrote inside this track&rsquo;s lessons, in track order.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-3 px-4 pb-4 sm:px-0 sm:pb-0">
          {!loaded ? (
            <p className="flex items-center gap-2 text-sm text-zinc-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading answers…
            </p>
          ) : courses.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">This track has no published courses.</p>
          ) : (
            courses.map((course, i) => (
              <CourseBlock key={course.id} course={course} defaultOpen={i === 0} />
            ))
          )}

          {loaded && (
            <p className="pt-2 text-xs text-zinc-500">
              <Link
                href={`/app/coach/members/${answers.memberId}?back=tracks`}
                className="underline hover:text-zinc-900"
              >
                Everything {memberName} has answered
              </Link>
            </p>
          )}
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
